import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import {
    updateTemplateCategory,
    deleteTemplateCategory,
    createTemplateItem,
} from "../lib/api";
import InlineEditText from "./InlineEditText";
import TemplateItemRow from "./TemplateItemRow";

export default function CategoryBlock({ category, onUpdated, onDeleted, collapseKey, expandKey }) {
    const [expanded, setExpanded] = useState(false);
    const [addingItem, setAddingItem] = useState(false);
    const [deleting, setDeleting] = useState(false);
    // Remember which items were just created in this session so they open
    // expanded for editing, and collapse on next render once the item is
    // "saved". We track by id.
    const [newlyCreatedIds, setNewlyCreatedIds] = useState(() => new Set());

    // Parent-driven collapse/expand signals.
    useEffect(() => {
        if (collapseKey === undefined) return;
        setExpanded(false);
    }, [collapseKey]);
    useEffect(() => {
        if (expandKey === undefined) return;
        setExpanded(true);
    }, [expandKey]);

    const saveName = async (name) => {
        try {
            const updated = await updateTemplateCategory(category.id, { name });
            onUpdated(updated);
        } catch (e) {
            toast.error("Couldn't rename category");
        }
    };

    const saveDescription = async (description) => {
        try {
            const updated = await updateTemplateCategory(category.id, {
                description,
            });
            onUpdated(updated);
        } catch (e) {
            toast.error("Couldn't save description");
        }
    };

    const handleDelete = async () => {
        if (!window.confirm(`Delete category "${category.name}" and all its items?`)) return;
        setDeleting(true);
        try {
            await deleteTemplateCategory(category.id);
            toast.success("Category deleted");
            onDeleted();
        } catch (e) {
            toast.error("Couldn't delete category");
            setDeleting(false);
        }
    };

    const handleAddItem = async () => {
        setAddingItem(true);
        try {
            const created = await createTemplateItem(category.id, {
                name: "",
                price_type: "per_item",
            });
            onUpdated({ items: [...(category.items || []), created] });
            setNewlyCreatedIds((prev) => {
                const next = new Set(prev);
                next.add(created.id);
                return next;
            });
        } catch (e) {
            toast.error("Couldn't add item");
        } finally {
            setAddingItem(false);
        }
    };

    const handleItemUpdated = (itemId, updatedItem) => {
        onUpdated({
            items: (category.items || []).map((i) => (i.id === itemId ? updatedItem : i)),
        });
    };

    const handleItemDeleted = (itemId) => {
        // Also drop any sub-items that were parented to this id (backend
        // cascade-deletes them too).
        onUpdated({
            items: (category.items || []).filter(
                (i) => i.id !== itemId && i.parent_item_id !== itemId,
            ),
        });
    };

    const handleSubItemCreated = (created) => {
        onUpdated({ items: [...(category.items || []), created] });
        setNewlyCreatedIds((prev) => {
            const next = new Set(prev);
            next.add(created.id);
            return next;
        });
    };

    const items = category.items || [];
    // Split into top-level items (no parent) and sub-items grouped by parent.
    const topLevel = items.filter((i) => !i.parent_item_id);
    const subsByParent = items.reduce((acc, i) => {
        if (i.parent_item_id) {
            (acc[i.parent_item_id] = acc[i.parent_item_id] || []).push(i);
        }
        return acc;
    }, {});
    Object.values(subsByParent).forEach((arr) =>
        arr.sort((a, b) => (a.rank || 0) - (b.rank || 0)),
    );

    return (
        <div
            className="bg-white/[0.05] border border-white/15 rounded-md overflow-hidden"
            data-testid={`category-${category.id}`}
        >
            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/15 bg-white/[0.05]">
                <button
                    onClick={() => setExpanded((v) => !v)}
                    className="p-1 hover:bg-white/10 rounded-sm text-slate-400 hover:text-white transition-colors"
                    data-testid={`category-toggle-${category.id}`}
                    aria-label={expanded ? "Collapse" : "Expand"}
                >
                    {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                <div className="flex-1 min-w-0">
                    <InlineEditText
                        value={category.name}
                        onSave={saveName}
                        placeholder="Category name"
                        className="text-base font-semibold text-white"
                        dataTestId={`category-name-${category.id}`}
                    />
                    {category.updated_by_name && (
                        <div
                            className="text-[10px] text-slate-500 tracking-tight mt-0.5 truncate"
                            data-testid={`category-editor-byline-${category.id}`}
                            title={`Last edited at ${category.updated_at}`}
                        >
                            Last edited by {category.updated_by_name} · {_relTimeCat(category.updated_at)}
                        </div>
                    )}
                </div>
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold shrink-0">
                    {topLevel.length} {topLevel.length === 1 ? "item" : "items"}
                </span>
                <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="p-1.5 rounded-sm text-slate-400 hover:bg-red-500/20 hover:text-red-300 transition-colors disabled:opacity-50"
                    data-testid={`category-delete-${category.id}`}
                    aria-label="Delete category"
                >
                    {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                </button>
            </div>

            {expanded && (
                <div className="p-3 space-y-3">
                    <div>
                        <label
                            className="block text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1"
                            htmlFor={`category-description-${category.id}`}
                        >
                            Description
                        </label>
                        <InlineEditText
                            value={category.description || ""}
                            onSave={saveDescription}
                            placeholder="Optional — e.g. 'All excavation services billed per hour of machine time.'"
                            className="text-sm text-slate-300 leading-snug"
                            multiline
                            dataTestId={`category-description-${category.id}`}
                        />
                    </div>
                    {topLevel.length === 0 ? (
                        <p className="text-xs text-slate-500 italic py-3 px-1">
                            No items yet — add your first service line below.
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {topLevel.map((item) => (
                                <TemplateItemRow
                                    key={item.id}
                                    item={item}
                                    defaultExpanded={newlyCreatedIds.has(item.id)}
                                    subItems={subsByParent[item.id] || []}
                                    onUpdated={(updated) => handleItemUpdated(item.id, updated)}
                                    onDeleted={() => handleItemDeleted(item.id)}
                                    onSubItemCreated={handleSubItemCreated}
                                    onSubItemUpdated={(subId, updated) =>
                                        handleItemUpdated(subId, updated)
                                    }
                                    onSubItemDeleted={(subId) => handleItemDeleted(subId)}
                                    collapseKey={collapseKey}
                                    /* iter-80: "Expand all" only opens
                                     * categories — item edit panels stay
                                     * closed so the user can scan all
                                     * line items at a glance. Collapse-
                                     * all still tidies up everything. */
                                />
                            ))}
                        </div>
                    )}

                    <button
                        onClick={handleAddItem}
                        disabled={addingItem}
                        data-testid={`category-add-item-${category.id}`}
                        className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-sm bg-[#1a1a1a] hover:bg-white/10 border border-dashed border-white/15 hover:border-[#1a3fd1]/60 text-xs font-semibold text-slate-200 hover:text-white transition-colors"
                    >
                        {addingItem ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                            <Plus className="w-3.5 h-3.5 text-[#8fa4f0]" />
                        )}
                        Add item
                    </button>
                </div>
            )}
        </div>
    );
}


function _relTimeCat(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const delta = Math.max(0, Date.now() - then);
    const m = Math.floor(delta / 60_000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return `${Math.floor(d / 7)}w ago`;
}
